import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Button,
  Spinner,
} from "@chakra-ui/react";
import React from "react";
import Card from "components/card";
import AppModal from "components/modal/AppModal";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { gmailDteInboxApi } from "api/endpoints";
import { useAuth } from "context/AuthContext";
import { useIdioma } from "stores/idiomaStore";
import ColumnSortHeader from "./ColumnSortHeader";
import ServerTablePagination from "./ServerTablePagination";
import { GmailInboxDetailFields } from "./GmailInboxDetailFields";
import RespuestaComercialModal from "./RespuestaComercialModal";
import PlazoSiiIndicator from "./PlazoSiiIndicator";
import FiltroRangoFechas from "components/filters/FiltroRangoFechas";

const columnHelper = createColumnHelper();

function formatDate(value) {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-CL", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

/**
 * Tiempo relativo compacto: "hace 2 h" / "2 h ago" / "2 小时前".
 * `tr` y `t` llegan desde el componente porque esta función vive afuera.
 * Sin ellos cae al español, que es como se comportaba antes.
 */
function formatRelative(value, tr, t) {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 0) return t ? t("tiempo.instantes") : "hace instantes";
  if (sec < 45) return t ? t("tiempo.instantes") : "hace instantes";
  const min = Math.round(sec / 60);
  if (min < 60) return tr ? tr(min, "tiempo.min") : `hace ${min} min`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return tr ? tr(hrs, "tiempo.hora") : `hace ${hrs} h`;
  const days = Math.round(hrs / 24);
  if (days < 30) return tr ? tr(days, days === 1 ? "tiempo.dia" : "tiempo.dias") : `hace ${days} ${days === 1 ? "día" : "días"}`;
  const months = Math.round(days / 30);
  if (months < 12) return tr ? tr(months, months === 1 ? "tiempo.mes" : "tiempo.meses") : `hace ${months} ${months === 1 ? "mes" : "meses"}`;
  const years = Math.round(months / 12);
  return tr ? tr(years, years === 1 ? "tiempo.anio" : "tiempo.anios") : `hace ${years} ${years === 1 ? "año" : "años"}`;
}

function formatMoney(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "—";
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(parsed);
}

const STATUS_CLAVES = {
  received: "opcion.recibido",
  parsed: "opcion.parseado",
  validated: "opcion.validado",
  accepted: "opcion.aceptado",
  rejected: "opcion.rechazado",
  error: "opcion.error",
};

// `t` llega desde fuera porque esta función vive afuera del componente.
function statusLabel(s, t) {
  const k = String(s ?? "").toLowerCase();
  const clave = STATUS_CLAVES[k];
  if (clave && t) return t(clave);
  return k ? k.toUpperCase() : s || "—";
}

function statusColorScheme(s) {
  const k = String(s ?? "").toLowerCase();
  const map = {
    received: "gray",
    parsed: "blue",
    validated: "cyan",
    accepted: "green",
    rejected: "orange",
    error: "red",
  };
  return map[k] || "gray";
}

function statusPillClasses(s) {
  const k = String(s ?? "").toLowerCase();
  const map = {
    received:
      "border-gray-300 bg-gray-100 text-gray-700 dark:border-white/20 dark:bg-white/10 dark:text-gray-200",
    parsed:
      "border-blue-300 bg-blue-100 text-blue-700 dark:border-blue-500/40 dark:bg-blue-500/20 dark:text-blue-200",
    validated:
      "border-cyan-300 bg-cyan-100 text-cyan-700 dark:border-cyan-500/40 dark:bg-cyan-500/20 dark:text-cyan-200",
    accepted:
      "border-emerald-300 bg-emerald-100 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/20 dark:text-emerald-200",
    rejected:
      "border-orange-300 bg-orange-100 text-orange-700 dark:border-orange-500/40 dark:bg-orange-500/20 dark:text-orange-200",
    error:
      "border-red-300 bg-red-100 text-red-700 dark:border-red-500/40 dark:bg-red-500/20 dark:text-red-200",
  };
  return map[k] || map.received;
}

function attachmentBadgeClass(kind) {
  if (kind === "XML") {
    return "border-emerald-300 bg-emerald-100 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/20 dark:text-emerald-200";
  }
  return "border-red-300 bg-red-100 text-red-700 dark:border-red-500/40 dark:bg-red-500/20 dark:text-red-200";
}

function resolveExternalAttachmentUrl(row, kind) {
  const candidate = kind === "XML"
    ? row?.incoming_xml_url
    : row?.incoming_supplier_pdf_url;
  const value = String(candidate || "").trim();
  return /^https?:\/\//i.test(value) ? value : "";
}

// Cada tipo del SII apunta a una clave del diccionario. Varios códigos
// distintos comparten nombre a propósito (33 y 34 son ambos "factura", una
// afecta y otra exenta), tal como estaba antes.
const DTE_TIPO_CLAVES = {
  30: "dte.factura",
  32: "dte.facturaExenta",
  33: "dte.factura",
  34: "dte.facturaExenta",
  35: "dte.boleta",
  38: "dte.boletaExenta",
  39: "dte.boleta",
  40: "dte.liquidacion",
  41: "dte.boletaExenta",
  43: "dte.liquidacion",
  45: "dte.facturaCompra",
  46: "dte.facturaCompra",
  48: "dte.pagoElectronico",
  50: "dte.guia",
  52: "dte.guia",
  55: "dte.notaDebito",
  56: "dte.notaDebito",
  60: "dte.notaCredito",
  61: "dte.notaCredito",
  110: "dte.facturaExportacion",
  111: "dte.notaDebitoExportacion",
  112: "dte.notaCreditoExportacion",
};

// Tipos de DTE que son "facturas" y que el receptor puede aceptar comercialmente.
// Las notas de crédito/débito (56/61) son informativas (devoluciones o
// correcciones de la factura) y NO se aceptan manualmente.
const FACTURA_TIPOS = new Set([33, 34, 46]);

function isFacturaTipo(tipo) {
  return FACTURA_TIPOS.has(Number(tipo));
}

// Ventana legal del SII: hay 8 días para aceptar/rechazar comercialmente. Pasado
// ese plazo, el SII acepta la factura automáticamente (aceptación tácita).
const SII_ACCEPT_DEADLINE_DAYS = 8;

function daysSince(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

// Pasada la ventana de 8 días el SII da la factura por aceptada, así que ya no
// se puede responder. En pantalla se muestra como "Aceptada por el SII", no
// como "vencida": la factura no caducó, se aceptó sola.
function isFacturaVencidaSii(item = {}) {
  if (!isFacturaTipo(item.tipo_dte)) return false;
  const status = String(item.processing_status || "").toLowerCase();
  if (status === "accepted" || status === "rejected") return false;
  const age = daysSince(item.received_at);
  return age != null && age > SII_ACCEPT_DEADLINE_DAYS;
}

/**
 * Días que quedan para responder antes de que el SII acepte la factura solo.
 * `null` cuando el plazo no aplica: no es una factura reclamable, ya se
 * respondió, o no hay fecha de recepción.
 *
 * Los cortes son los mismos que usa el filtro del backend (plazoSii.js). Si
 * se cambian acá y allá no, la pantalla dice una cosa y el servidor otra.
 */
function plazoRestante(item = {}) {
  if (!isFacturaTipo(item.tipo_dte)) return null;
  const status = String(item.processing_status || "").toLowerCase();
  if (status === "accepted" || status === "rejected") return null;
  const edad = daysSince(item.received_at);
  if (edad == null || edad < 0) return null;
  return SII_ACCEPT_DEADLINE_DAYS - edad;
}

// Las cuatro ventanas del plazo. `dias` es el corte que entiende el backend.
// Grupos de tipo de documento, en orden de importancia para quien revisa: las
// facturas son el grueso y lo único con plazo del SII corriendo; las notas
// corrigen una factura anterior y las guías ni siquiera son un gasto.
const GRUPOS_TIPO = [
  { clave: "", claveI18n: "grupo.todos" },
  { clave: "facturas", claveI18n: "grupo.facturas", contador: "facturas" },
  { clave: "guias", claveI18n: "grupo.guias", contador: "guias" },
  { clave: "notas_credito", claveI18n: "grupo.notasCredito", contador: "notas_credito" },
  { clave: "notas_debito", claveI18n: "grupo.notasDebito", contador: "notas_debito" },
];

const VENTANAS_SII = [
  { clave: "", claveTexto: "plazo.todas" },
  { clave: "criticas", claveTexto: "plazo.criticas", tono: "rojo" },
  { clave: "por_vencer", claveTexto: "plazo.porVencer", tono: "ambar" },
  { clave: "recientes", claveTexto: "plazo.recientes", tono: "verde" },
  { clave: "vencidas", claveTexto: "plazo.vencidas", tono: "gris" },
];

const TONOS_VENTANA = {
  rojo: "border-red-500 bg-red-50 text-red-700 dark:border-red-400 dark:bg-red-400/10 dark:text-red-200",
  ambar: "border-amber-500 bg-amber-50 text-amber-800 dark:border-amber-400 dark:bg-amber-400/10 dark:text-amber-200",
  verde: "border-emerald-500 bg-emerald-50 text-emerald-700 dark:border-emerald-400 dark:bg-emerald-400/10 dark:text-emerald-200",
  gris: "border-gray-400 bg-gray-100 text-gray-700 dark:border-white/20 dark:bg-white/10 dark:text-gray-200",
};

function dteTipoLabel(tipo, t) {
  if (tipo == null || tipo === "") return "";
  const n = Number(tipo);
  const clave = DTE_TIPO_CLAVES[n];
  // Un tipo que el SII agregue y que aquí no esté se muestra por su número,
  // que es información útil, en vez de quedar en blanco.
  if (!clave || !t) return `${t ? t("col.tipo") : "Tipo"} ${n}`;
  return t(clave);
}

// Fechas ISO locales (YYYY-MM-DD) para el filtro de rango por fecha recibida.
function isoTodayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function isoMonthStartLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function downloadBlob(blob, filename) {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename || "compras-detalle.xlsx";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

const LIST_FILTERS_INITIAL = () => ({
  processing_status: "",
  from_email: "",
  // Búsqueda del día a día del contador: número de documento y proveedor.
  folio: "",
  razon_social: "",
  // Ventana del plazo del SII: "", criticas, por_vencer, recientes, vencidas.
  ventana_sii: "",
  // Grupo de tipo: "", facturas, guias, notas_credito, notas_debito.
  grupo_tipo: "",
  limit: 50,
  sort_by: "received_at",
  sort_dir: "desc",
});

const LIMIT_OPTIONS = [50, 100, 200];
// Las etiquetas se guardan como claves del diccionario y se traducen al
// pintarlas, para que el selector cambie de idioma con la interfaz.
const STATUS_OPTS = [
  { value: "", label: "opcion.todosEstados" },
  { value: "received", label: "opcion.recibido" },
  { value: "parsed", label: "opcion.parseado" },
  { value: "validated", label: "opcion.validado" },
  { value: "accepted", label: "opcion.aceptado" },
  { value: "rejected", label: "opcion.rechazado" },
  { value: "error", label: "opcion.error" },
];

export default function GmailDteComprasTable() {
  const { t, tRelativo } = useIdioma();
  // Quien no puede responder no debe ver el botón: antes aparecía para todos y
  // el contador llegaba hasta escribir el motivo para recibir un 403.
  const { puedeResponderCompras } = useAuth();
  const [listFilters, setListFilters] = React.useState(LIST_FILTERS_INITIAL);
  // Rango de fechas por fecha recibida. Por defecto: mes actual.
  const [dateDesde, setDateDesde] = React.useState(isoMonthStartLocal);
  const [dateHasta, setDateHasta] = React.useState(isoTodayLocal);
  const [exporting, setExporting] = React.useState(false);
  const [page, setPage] = React.useState(1);

  const [data, setData] = React.useState([]);
  // Totales del conjunto filtrado, calculados en el servidor.
  const [resumen, setResumen] = React.useState(null);

  // Pestaña activa. "compras" es lo que se opera; "acuses" son las respuestas
  // que mandan los clientes cuando reciben una factura nuestra: no son compras,
  // no tienen proveedor ni monto, y no hay nada que responderles. Antes se
  // mezclaban con las compras y hasta mostraban el plazo del SII corriendo.
  const [vista, setVista] = React.useState("compras");
  const [listMeta, setListMeta] = React.useState({
    total: 0,
    total_pages: 0,
    page: 1,
  });
  const [loading, setLoading] = React.useState(true);
  const [resyncing, setResyncing] = React.useState(false);
  const [pdfRegeneratingById, setPdfRegeneratingById] = React.useState({});
  const [reprocessingById, setReprocessingById] = React.useState({});
  const [manualAcceptingById, setManualAcceptingById] = React.useState({});
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [lastSyncAt, setLastSyncAt] = React.useState(null);

  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailItem, setDetailItem] = React.useState(null);
  const [externalLinkPrompt, setExternalLinkPrompt] = React.useState({
    open: false,
    url: "",
    hostname: "",
    kind: "",
  });
  const externalLinkCancelRef = React.useRef(null);

  const applyColumnSort = React.useCallback((key, dir) => {
    setPage(1);
    setListFilters((f) => ({ ...f, sort_by: key, sort_dir: dir }));
  }, []);

  const patchListFilters = React.useCallback((patch) => {
    setPage(1);
    setListFilters((f) => ({ ...f, ...patch }));
  }, []);

  const fetchList = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const query = {
        limit: Math.min(
          200,
          Math.max(1, Number(listFilters.limit) || 50),
        ),
        page: Math.max(1, page),
        sort_by: String(listFilters.sort_by || "received_at"),
        sort_dir: String(listFilters.sort_dir || "desc"),
        // Solo compras DTE reales (con XML adjunto) y con un tipo de documento
        // válido detectado en el XML. Deja fuera correos que no son DTE
        // (alertas de seguridad, spam) y filas sin tipo (no aportan nada).
        has_xml: true,
        tipo_dte_present: true,
      };
      const st = String(listFilters.processing_status ?? "").trim();
      if (st) query.processing_status = st;
      const from = String(listFilters.from_email ?? "").trim();
      if (from) query.from_email = from;
      const folio = String(listFilters.folio ?? "").trim();
      if (folio) query.folio = folio;
      const proveedor = String(listFilters.razon_social ?? "").trim();
      if (proveedor) query.razon_social = proveedor;
      const ventana = String(listFilters.ventana_sii ?? "").trim();
      if (ventana) query.ventana_sii = ventana;
      const grupo = String(listFilters.grupo_tipo ?? "").trim();
      if (grupo) query.grupo_tipo = grupo;
      if (vista === "acuses") {
        query.clase = "acuse_recibo";
      } else {
        // Los correos anteriores a la clasificación tienen clase NULL; el
        // backend los cuenta como compra, así que siguen apareciendo acá.
        query.excluir_clase = "acuse_recibo";
      }
      if (dateDesde) query.received_desde = dateDesde;
      if (dateHasta) query.received_hasta = dateHasta;

      // El resumen va en paralelo y con los MISMOS filtros: son los totales del
      // conjunto filtrado, no de la página. Si falla, la tabla igual se muestra:
      // las tarjetas quedan en blanco, que es mejor que no ver las compras.
      const [{ res, data: payload }, resumenRes] = await Promise.all([
        gmailDteInboxApi.list(query),
        gmailDteInboxApi.resumen(query).catch(() => null),
      ]);
      const r = resumenRes?.data;
      setResumen(r?.ok ? r : null);
      if (!res.ok || !payload?.ok) {
        setError(payload?.error || payload?.message || `Error ${res.status}`);
        setData([]);
        setListMeta({ total: 0, total_pages: 0, page: 1 });
        return;
      }
      setData(Array.isArray(payload.items) ? payload.items : []);
      setListMeta({
        total: Number(payload?.total ?? 0),
        total_pages: Number(payload?.total_pages ?? 0),
        page: Number(payload?.page ?? page),
      });
      setLastSyncAt(new Date().toISOString());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error de red.");
      setData([]);
      setListMeta({ total: 0, total_pages: 0, page: 1 });
    } finally {
      setLoading(false);
    }
  }, [listFilters, page, dateDesde, dateHasta, vista]);

  // Export Excel a NIVEL DETALLE: una fila por producto de cada compra.
  const handleExportDetail = React.useCallback(async () => {
    setExporting(true);
    setError("");
    setNotice("");
    try {
      const qs = new URLSearchParams();
      const st = String(listFilters.processing_status ?? "").trim();
      if (st) qs.set("processing_status", st);
      const from = String(listFilters.from_email ?? "").trim();
      if (from) qs.set("from_email", from);
      const folioX = String(listFilters.folio ?? "").trim();
      if (folioX) qs.set("folio", folioX);
      const proveedorX = String(listFilters.razon_social ?? "").trim();
      if (proveedorX) qs.set("razon_social", proveedorX);
      const ventanaX = String(listFilters.ventana_sii ?? "").trim();
      if (ventanaX) qs.set("ventana_sii", ventanaX);
      const grupoX = String(listFilters.grupo_tipo ?? "").trim();
      if (grupoX) qs.set("grupo_tipo", grupoX);
      if (dateDesde) qs.set("received_desde", dateDesde);
      if (dateHasta) qs.set("received_hasta", dateHasta);
      const { res, blob, data, filename } = await gmailDteInboxApi.exportDetailXlsx(`?${qs}`);
      if (!res.ok || !blob) {
        throw new Error(data?.error || data?.message || `Error ${res.status} al exportar el detalle.`);
      }
      downloadBlob(blob, filename || "dte-compras-detalle.xlsx");
      const rowCount = res.headers.get("X-Export-Row-Count");
      const totalMatching = res.headers.get("X-Export-Total-Matching");
      const truncated = res.headers.get("X-Export-Truncated") === "true";
      setNotice(
        truncated
          ? `Excel de detalle generado: ${rowCount ?? "?"} líneas (de ${totalMatching ?? "?"} compras; se aplicó el límite).`
          : `Excel de detalle generado: ${rowCount ?? "?"} líneas de producto.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al exportar el detalle.");
    } finally {
      setExporting(false);
    }
  }, [listFilters, dateDesde, dateHasta]);

  const handleResync = React.useCallback(async () => {
    setResyncing(true);
    setError("");
    setNotice("");
    try {
      const { res, data: payload } = await gmailDteInboxApi.resync();
      if (!res.ok || !payload?.ok) {
        setError(payload?.error || payload?.message || `Error ${res.status} al sincronizar Gmail.`);
        return;
      }
      const processed = Array.isArray(payload?.gmail?.inboxItems) ? payload.gmail.inboxItems.length : 0;
      const stage = String(payload?.stage || "gmail-resync");
      setNotice(`Sincronización OK (${stage}). Registros procesados: ${processed}.`);
      await fetchList();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error de red al sincronizar Gmail.");
    } finally {
      setResyncing(false);
    }
  }, [fetchList]);

  const handleRegeneratePdf = React.useCallback(
    async (id) => {
      setPdfRegeneratingById((prev) => ({ ...prev, [id]: true }));
      setError("");
      setNotice("");
      try {
        const { res, data: payload } = await gmailDteInboxApi.regeneratePdf(id);
        if (!res.ok || !payload?.ok) {
          setError(payload?.error || payload?.message || `Error ${res.status} generando PDF.`);
          return;
        }
        setNotice("PDF regenerado correctamente.");
        await fetchList();
      } catch (e) {
        setError(e instanceof Error ? e.message : "No se pudo regenerar el PDF.");
      } finally {
        setPdfRegeneratingById((prev) => ({ ...prev, [id]: false }));
      }
    },
    [fetchList],
  );

  // Reproceso de una compra que quedó en "error". Vuelve a bajar el correo
  // original desde Gmail y lo procesa de cero: es la única salida cuando la
  // ficha quedó sin XML guardado, porque "Generar PDF" y la respuesta comercial
  // leen ese XML y no lo encuentran.
  const handleReprocess = React.useCallback(
    async (id) => {
      if (!window.confirm(t("reproceso.confirmar"))) return;
      setReprocessingById((prev) => ({ ...prev, [id]: true }));
      setError("");
      setNotice("");
      try {
        const { res, data: payload } = await gmailDteInboxApi.reprocess(id);
        if (!res.ok) {
          setError(payload?.error || payload?.message || `Error ${res.status} al reprocesar la compra.`);
          return;
        }
        // El backend responde 200 con ok:false cuando la compra volvió a quedar
        // en error. El motivo nuevo viene en la nota y es más útil que un
        // mensaje genérico: puede ser un problema distinto al original.
        if (!payload?.ok) {
          setError(
            payload?.item?.processing_note
              ? `${t("reproceso.sigueEnError")} ${payload.item.processing_note}`
              : t("reproceso.falloSinDetalle"),
          );
          await fetchList();
          return;
        }
        setNotice(t("reproceso.ok"));
        await fetchList();
      } catch (e) {
        setError(e instanceof Error ? e.message : "No se pudo reprocesar la compra.");
      } finally {
        setReprocessingById((prev) => ({ ...prev, [id]: false }));
      }
    },
    [fetchList, t],
  );

  // Abre el modal de respuesta comercial. La confirmación vive en el modal y
  // no en un window.confirm: hay que elegir entre tres decisiones distintas y
  // escribir un motivo, no responder "aceptar / cancelar".
  const [respuestaDoc, setRespuestaDoc] = React.useState(null);

  const abrirRespuesta = React.useCallback((row) => {
    setRespuestaDoc(row || null);
  }, []);

  const handleRespuestaComercial = React.useCallback(
    async ({ accion, motivo }) => {
      const id = respuestaDoc?.id;
      if (!id) return;
      setManualAcceptingById((prev) => ({ ...prev, [id]: true }));
      setError("");
      setNotice("");
      try {
        const { res, data: payload } = await gmailDteInboxApi.respuestaComercial(id, {
          accion,
          motivo,
        });
        if (!res.ok || !payload?.ok) {
          setError(
            payload?.error || payload?.message || `Error ${res.status} al responder al proveedor.`,
          );
          return;
        }
        setNotice(payload.alreadyResponded ? t("respuesta.yaRespondida") : t("respuesta.enviada"));
        setRespuestaDoc(null);
        await fetchList();
      } catch (e) {
        setError(e instanceof Error ? e.message : "No se pudo enviar la respuesta.");
      } finally {
        setManualAcceptingById((prev) => ({ ...prev, [id]: false }));
      }
    },
    [fetchList, respuestaDoc, t],
  );

  React.useEffect(() => {
    fetchList();
  }, [fetchList]);

  const pageSize = Math.min(
    200,
    Math.max(1, Number(listFilters.limit) || 50),
  );


  const openDetail = React.useCallback(async (id) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailItem(null);
    try {
      const { res, data } = await gmailDteInboxApi.get(id);
      if (!res.ok || !data?.ok || !data.item) {
        setDetailItem(null);
        return;
      }
      setDetailItem(data.item);
    } catch {
      setDetailItem(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeExternalLinkPrompt = React.useCallback(() => {
    setExternalLinkPrompt({
      open: false,
      url: "",
      hostname: "",
      kind: "",
    });
  }, []);

  const openExternalLinkPrompt = React.useCallback((url, kind = "") => {
    const safeUrl = String(url || "").trim();
    if (!safeUrl) return;
    let hostname = "";
    try {
      hostname = new URL(safeUrl).hostname;
    } catch {
      hostname = "";
    }
    setExternalLinkPrompt({
      open: true,
      url: safeUrl,
      hostname,
      kind: String(kind || "").trim(),
    });
  }, []);

  const confirmOpenExternalLink = React.useCallback(() => {
    const safeUrl = String(externalLinkPrompt.url || "").trim();
    if (!safeUrl) {
      closeExternalLinkPrompt();
      return;
    }
    window.open(safeUrl, "_blank", "noopener,noreferrer");
    closeExternalLinkPrompt();
  }, [externalLinkPrompt.url, closeExternalLinkPrompt]);

  const handleOpenExternalAttachment = React.useCallback(async (e, { url, kind, id }) => {
    e.stopPropagation();

    // El XML vive en un bucket privado —son documentos tributarios de terceros,
    // con RUT y montos de proveedores— así que el enlace directo a S3 devuelve
    // "permiso denegado". Se baja por la API, que valida la sesión, y se abre
    // desde la memoria del navegador.
    if (kind === "XML" && id != null) {
      try {
        const { res, blob, data } = await gmailDteInboxApi.descargarXml(String(id));
        if (!res.ok || !blob) {
          setError(data?.error || data?.message || `No se pudo abrir el XML (error ${res.status}).`);
          return;
        }
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, "_blank", "noopener");
        // Se libera después de que el navegador alcanzó a abrirlo.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Error de red al abrir el XML.");
      }
      return;
    }

    const finalUrl = String(url || "").trim();
    if (!finalUrl) return;
    openExternalLinkPrompt(finalUrl, kind);
  }, [openExternalLinkPrompt]);

  // Idioma de la interfaz. `t` viene memoizada contra el idioma, así que sirve
  // como dependencia estable del useMemo de columnas.

  const columns = React.useMemo(
    () => [
      columnHelper.accessor("id", {
        header: () => (
          <ColumnSortHeader
            label="ID"
            sortKey="id"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <button
            type="button"
            className="text-sm font-medium text-brand-600 underline-offset-2 hover:underline dark:text-brand-300"
            onClick={() => openDetail(info.getValue())}
          >
            {info.getValue()}
          </button>
        ),
      }),
      columnHelper.accessor("received_at", {
        header: () => (
          <ColumnSortHeader
            label={t("col.recibido")}
            sortKey="received_at"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span
            className="whitespace-nowrap text-sm text-navy-700 dark:text-white"
            title={formatDate(info.getValue())}
          >
            {formatRelative(info.getValue(), tRelativo, t)}
          </span>
        ),
      }),
      columnHelper.accessor("razon_social", {
        header: () => t("col.proveedor"),
        cell: (info) => (
          <span
            className="block max-w-[240px] truncate text-sm font-medium text-navy-700 dark:text-white"
            title={info.getValue() || ""}
          >
            {info.getValue() || "—"}
          </span>
        ),
      }),
      columnHelper.accessor("rut_emisor", {
        header: () => t("col.rut"),
        cell: (info) => (
          <span className="whitespace-nowrap text-sm tabular-nums text-navy-700 dark:text-white">
            {info.getValue() || "—"}
          </span>
        ),
      }),
      // El folio del documento del proveedor. Es el número por el que se busca
      // una compra cuando alguien pregunta por ella, y no estaba en la tabla:
      // lo que se veía era el código de envío, que es otra cosa.
      columnHelper.accessor("folio", {
        header: () => (
          <ColumnSortHeader
            label={t("col.folio")}
            sortKey="folio"
            activeKey={listFilters.sort_by}
            activeDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => {
          const v = info.getValue();
          // El proveedor reenvía la misma factura y cada correo creaba una
          // fila. Ahora se muestra una sola, pero en silencio parecería que
          // faltan correos: el distintivo dice cuántas copias llegaron.
          const copias = Number(info.row.original?.copias || 0);
          return (
            <span className="flex items-center gap-2">
              <span className="text-sm font-medium text-navy-700 dark:text-white">
                {v != null && String(v).trim() !== "" ? v : "—"}
              </span>
              {copias > 0 ? (
                <span
                  title={t("compras.copiasAyuda").replace("{n}", String(copias))}
                  className="whitespace-nowrap rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-navy-700 dark:text-gray-300"
                >
                  {t("compras.copias").replace("{n}", String(copias))}
                </span>
              ) : null}
            </span>
          );
        },
      }),
      columnHelper.accessor("tipo_dte", {
        header: () => t("col.tipo"),
        cell: (info) => {
          const label = dteTipoLabel(info.getValue(), t);
          return (
            <span
              className="block max-w-[200px] truncate text-sm text-navy-700 dark:text-white"
              title={label}
            >
              {label || "—"}
            </span>
          );
        },
      }),
      columnHelper.accessor("processing_status", {
        header: () => (
          <ColumnSortHeader
            label={t("col.estado")}
            sortKey="processing_status"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => {
          const v = info.getValue();
          const fila = info.row.original || {};
          const vencida = isFacturaVencidaSii(fila);
          const restante = plazoRestante(fila);
          return (
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge colorScheme={statusColorScheme(v)} borderRadius="md" px={2}>
                {statusLabel(v, t)}
              </Badge>
              {/* Escala de verde a rojo según lo que queda del plazo. Se
                  muestra en todas las facturas con plazo vivo, no solo en las
                  urgentes: la gracia de una escala es poder comparar filas de
                  un vistazo, y para eso tienen que estar todas. */}
              {/* Un acuse no tiene plazo: es la respuesta del cliente a una
                  factura nuestra, no algo que debamos contestar. */}
              {String(fila.clase || "") === "acuse_recibo" ? null : (
                <PlazoSiiIndicator diasRestantes={vencida ? -1 : restante} />
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor("cod_envio", {
        header: () => (
          <ColumnSortHeader
            label={t("col.codEnvio")}
            sortKey="cod_envio"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm tabular-nums text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("monto_total", {
        header: () => t("col.totalCompra"),
        cell: (info) => (
          <span className="text-sm font-semibold tabular-nums text-navy-700 dark:text-white">
            {formatMoney(info.getValue())}
          </span>
        ),
      }),
      columnHelper.display({
        id: "adj",
        header: () => t("col.adjuntos"),
        cell: ({ row }) => {
          const xml = row.original?.has_xml ? "XML" : null;
          const pdf = row.original?.has_pdf ? "PDF" : null;
          const bits = [xml, pdf].filter(Boolean);
          return (
            <div className="flex flex-wrap gap-1">
              {bits.length ? (
                bits.map((kind) => (
                  <button
                    key={`${row.original.id}-${kind}`}
                    type="button"
                    onClick={(e) =>
                      handleOpenExternalAttachment(e, {
                        id: row.original?.id,
                        url: resolveExternalAttachmentUrl(row.original, kind),
                        kind,
                      })
                    }
                    title={
                      resolveExternalAttachmentUrl(row.original, kind)
                        ? `Abrir ${kind} en enlace externo (con advertencia)`
                        : `${kind} sin enlace externo disponible`
                    }
                    disabled={!resolveExternalAttachmentUrl(row.original, kind)}
                    className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-45 ${attachmentBadgeClass(kind)}`}
                  >
                    {kind}
                  </button>
                ))
              ) : (
                <span className="text-xs text-navy-700 dark:text-gray-300">—</span>
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "acciones",
        header: "",
        cell: ({ row }) => {
          const item = row.original || {};
          const id = item.id;
          const status = String(item.processing_status || "").toLowerCase();
          const shouldShowRegeneratePdf = status === "accepted" && !Boolean(item.has_pdf);
          // Solo facturas, dentro de la ventana de 8 días. Pasado ese plazo el
          // SII ya la aceptó automáticamente → no se puede aceptar manual.
          // Un acuse no se responde: es la respuesta del cliente a una factura
          // nuestra. No hay proveedor esperando nada.
          const esAcuse = String(item.clase || "") === "acuse_recibo";
          const shouldShowManualAccept =
            !esAcuse
            && puedeResponderCompras
            && status === "validated" && isFacturaTipo(item.tipo_dte) && !isFacturaVencidaSii(item);
          const shouldShowReprocess = status === "error";
          const regenBusy = Boolean(pdfRegeneratingById[id]);
          const acceptBusy = Boolean(manualAcceptingById[id]);
          const reprocessBusy = Boolean(reprocessingById[id]);
          return (
            <div className="flex items-center gap-2">
              <Button
                size="xs"
                variant="outline"
                colorScheme="brand"
                onClick={(e) => {
                  e.stopPropagation();
                  openDetail(id);
                }}
              >
                {t("accion.ver")}
              </Button>
              {shouldShowRegeneratePdf ? (
                <Button
                  size="xs"
                  variant="solid"
                  colorScheme="green"
                  isLoading={regenBusy}
                  loadingText={t("accion.generando")}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleRegeneratePdf(id);
                  }}
                >
                  {t("accion.generarPdf")}
                </Button>
              ) : null}
              {shouldShowReprocess ? (
                <Button
                  size="xs"
                  variant="solid"
                  colorScheme="orange"
                  isLoading={reprocessBusy}
                  loadingText={t("accion.reprocesando")}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleReprocess(id);
                  }}
                >
                  {t("accion.reprocesar")}
                </Button>
              ) : null}
              {shouldShowManualAccept ? (
                <Button
                  size="xs"
                  variant="solid"
                  colorScheme="teal"
                  isLoading={acceptBusy}
                  loadingText={t("accion.aceptando")}
                  onClick={(e) => {
                    e.stopPropagation();
                    abrirRespuesta(item);
                  }}
                >
                  {t("respuesta.responder")}
                </Button>
              ) : null}
            </div>
          );
        },
      }),
    ],
    [
      applyColumnSort,
      listFilters.sort_by,
      listFilters.sort_dir,
      openDetail,
      handleOpenExternalAttachment,
      pdfRegeneratingById,
      manualAcceptingById,
      puedeResponderCompras,
      reprocessingById,
      handleRegeneratePdf,
      handleReprocess,
      abrirRespuesta,
      t,
      tRelativo,
    ],
  );

  const table = useReactTable({
    data,
    columns,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id),
  });

  const showBlockingLoading = loading && data.length === 0;

  return (
    <>
      <Card extra="w-full p-4 sm:p-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              {t("compras.titulo")}
            </h4>
            <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-400">
              {t("compras.descripcion")}
            </p>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t("compras.ultimaSync")}:{" "}
              {lastSyncAt ? formatDate(lastSyncAt) : t("compras.sinEjecutar")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => fetchList()}
              disabled={loading || resyncing}
              className="linear shrink-0 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400"
            >
              {loading ? t("accion.actualizando") : t("accion.actualizar")}
            </button>
            <button
              type="button"
              onClick={handleResync}
              disabled={loading || resyncing}
              className="linear shrink-0 rounded-xl border border-brand-500 bg-white px-4 py-2.5 text-sm font-medium text-brand-600 transition hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-brand-300 dark:bg-navy-900 dark:text-brand-200 dark:hover:bg-navy-800"
            >
              {resyncing ? t("accion.sincronizando") : t("accion.resincronizar")}
            </button>
            <button
              type="button"
              onClick={handleExportDetail}
              disabled={loading || exporting}
              title={t("accion.exportarDetalleAyuda")}
              className="linear shrink-0 rounded-xl border border-emerald-500 bg-white px-4 py-2.5 text-sm font-medium text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-400 dark:bg-navy-900 dark:text-emerald-200 dark:hover:bg-navy-800"
            >
              {exporting ? t("accion.exportando") : t("accion.exportarDetalle")}
            </button>
          </div>
        </div>
        {/* Resumen del conjunto FILTRADO, calculado en el servidor. Las tarjetas
            anteriores contaban `data.length`, o sea la PÁGINA cargada: con 358
            compras y 50 por página mostraban 50. Para un conteo pasaba; para una
            suma de dinero habría sido un número falso. */}
        <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900/60">
            <p className="text-[11px] uppercase text-gray-500 dark:text-gray-400">
              {t("kpi.totalComprado")}
            </p>
            <p className="text-lg font-bold text-navy-700 dark:text-white">
              {resumen ? formatMoney(resumen.monto_neto) : "—"}
            </p>
            {resumen?.notas_credito > 0 ? (
              <p className="text-[10px] text-gray-500 dark:text-gray-400">
                {t("kpi.netoConNotas")}
              </p>
            ) : null}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900/60">
            <p className="text-[11px] uppercase text-gray-500 dark:text-gray-400">
              {t("kpi.compras")}
            </p>
            <p className="text-lg font-bold text-navy-700 dark:text-white">
              {resumen ? resumen.total : listMeta.total || "—"}
            </p>
          </div>

          <div className="rounded-xl border border-blue-300 bg-blue-50 px-3 py-2 dark:border-blue-500/40 dark:bg-blue-500/10">
            <p className="text-[11px] uppercase text-blue-700 dark:text-blue-300">
              {t("kpi.porResponder")}
            </p>
            <p className="text-lg font-bold text-blue-800 dark:text-blue-100">
              {resumen ? resumen.por_responder : "—"}
            </p>
          </div>

          {/* La cuarta tarjeta cambia según lo que necesite atención. Lo urgente
              manda sobre lo informativo: primero el plazo del SII, después las
              compras cuyo monto no se pudo leer (que hacen que el total de
              arriba esté incompleto), y si no hay nada de eso, los errores. */}
          {resumen?.criticas > 0 ? (
            <div className="rounded-xl border border-red-300 bg-red-50 px-3 py-2 dark:border-red-500/40 dark:bg-red-500/10">
              <p className="text-[11px] uppercase text-red-700 dark:text-red-300">
                {t("kpi.criticas")}
              </p>
              <p className="text-lg font-bold text-red-800 dark:text-red-100">{resumen.criticas}</p>
              <p className="text-[10px] text-red-700 dark:text-red-300">{t("kpi.criticasPie")}</p>
            </div>
          ) : resumen?.sin_monto > 0 ? (
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-500/40 dark:bg-amber-400/10">
              <p className="text-[11px] uppercase text-amber-700 dark:text-amber-300">
                {t("kpi.sinMonto")}
              </p>
              <p className="text-lg font-bold text-amber-800 dark:text-amber-100">{resumen.sin_monto}</p>
              <p className="text-[10px] text-amber-700 dark:text-amber-300">{t("kpi.sinMontoPie")}</p>
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900/60">
              <p className="text-[11px] uppercase text-gray-500 dark:text-gray-400">
                {t("kpi.errores")}
              </p>
              <p className="text-lg font-bold text-navy-700 dark:text-white">
                {resumen ? resumen.con_error : "—"}
              </p>
            </div>
          )}
        </div>

        {showBlockingLoading ? (
          <div className="mt-8 flex flex-col items-center gap-3">
            <Spinner color="brand.500" size="lg" thickness="4px" />
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Cargando bandeja…
            </p>
          </div>
        ) : null}

        {error ? (
          <p
            className="mt-4 rounded-lg border border-red-400/40 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-200"
            role="alert"
          >
            {error}
          </p>
        ) : null}
        {notice ? (
          <p
            className="mt-4 rounded-lg border border-emerald-400/40 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-200"
            role="status"
          >
            {notice}
          </p>
        ) : null}

        {!showBlockingLoading && !error ? (
          <>
            <div className="mt-4 rounded-xl border border-gray-200 bg-lightPrimary/50 p-4 dark:border-white/10 dark:bg-navy-900/30">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                  {t("filtro.titulo")}
                </div>
                <button
                  type="button"
                  className="rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-navy-700 hover:bg-gray-50 dark:border-white/20 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-800"
                  onClick={() => {
                    setPage(1);
                    setListFilters(LIST_FILTERS_INITIAL());
                  }}
                >
                  {t("filtro.limpiar")}
                </button>
              </div>
              {/* Compras y acuses son cosas distintas y se miran por separado.
                  Un acuse es la respuesta de un CLIENTE a una factura que le
                  emitimos: no tiene proveedor ni monto, y no hay que
                  responderlo. Mezclarlos ensuciaba la lista y el total. */}
              <div className="mt-3 flex flex-wrap gap-2 border-b border-gray-200 pb-3 dark:border-white/10">
                {[
                  { clave: "compras", etiqueta: t("vista.compras") },
                  { clave: "acuses", etiqueta: t("vista.acuses") },
                ].map((v) => {
                  const activa = vista === v.clave;
                  return (
                    <button
                      key={v.clave}
                      type="button"
                      onClick={() => {
                        if (activa) return;
                        setPage(1);
                        setVista(v.clave);
                      }}
                      aria-pressed={activa}
                      className={[
                        "rounded-lg px-3 py-1.5 text-sm font-semibold transition",
                        activa
                          ? "bg-brand-500 text-white dark:bg-brand-400"
                          : "border border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-white/15 dark:text-gray-300 dark:hover:bg-white/5",
                      ].join(" ")}
                    >
                      {v.etiqueta}
                    </button>
                  );
                })}
              </div>

              {vista === "acuses" ? (
                <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-600 dark:bg-white/5 dark:text-gray-300">
                  {t("vista.acusesAyuda")}
                </p>
              ) : null}

              {vista === "compras" ? (
              <div className="mt-3">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {t("grupo.titulo")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {GRUPOS_TIPO.map((g) => {
                    const activo = String(listFilters.grupo_tipo || "") === g.clave;
                    // El número sale del resumen, que usa los MISMOS filtros de
                    // fecha y estado que la tabla: es cuántos hay en lo que se
                    // está mirando, no en todo el buzón.
                    const cuantos = g.contador ? resumen?.por_grupo?.[g.contador] : resumen?.total;
                    return (
                      <button
                        key={g.clave || "todos"}
                        type="button"
                        onClick={() => {
                          setPage(1);
                          patchListFilters({ grupo_tipo: activo && g.clave ? "" : g.clave });
                        }}
                        aria-pressed={activo}
                        className={[
                          "rounded-lg px-3 py-1.5 text-xs font-semibold transition",
                          activo
                            ? "bg-navy-700 text-white dark:bg-white/20"
                            : "border border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-white/15 dark:text-gray-300 dark:hover:bg-white/5",
                        ].join(" ")}
                      >
                        {t(g.claveI18n)}
                        {cuantos != null ? (
                          <span className={activo ? "ml-1.5 opacity-80" : "ml-1.5 text-gray-400"}>
                            {cuantos}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
              ) : null}

              {vista === "compras" ? (
              <div className="mt-3">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {t("plazo.titulo")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {VENTANAS_SII.map((v) => {
                    const activa = String(listFilters.ventana_sii || "") === v.clave;
                    return (
                      <button
                        key={v.clave || "todas"}
                        type="button"
                        onClick={() => {
                          setPage(1);
                          setListFilters((f) => ({
                            ...f,
                            ventana_sii: activa ? "" : v.clave,
                          }));
                        }}
                        aria-pressed={activa}
                        className={[
                          "rounded-lg border px-3 py-1.5 text-xs font-semibold transition",
                          activa && v.tono
                            ? TONOS_VENTANA[v.tono]
                            : activa
                              ? "border-brand-500 bg-brand-500 text-white dark:border-brand-400 dark:bg-brand-400"
                              : "border-gray-300 bg-white text-navy-700 hover:bg-gray-50 dark:border-white/20 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-800",
                        ].join(" ")}
                      >
                        {t(v.claveTexto)}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
                  {t("plazo.explicacion")}
                </p>
              </div>
              ) : null}

              <div className="mt-4 flex flex-wrap gap-2">
                {STATUS_OPTS.filter((s) => s.value).map((s) => {
                  const active = String(listFilters.processing_status || "") === s.value;
                  return (
                    <button
                      key={`quick-status-${s.value}`}
                      type="button"
                      onClick={() => patchListFilters({ processing_status: active ? "" : s.value })}
                      className={`rounded-lg border px-2.5 py-1 text-xs font-semibold transition ${active ? statusPillClasses(s.value) : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50 dark:border-white/20 dark:bg-navy-900 dark:text-gray-300 dark:hover:bg-navy-800"}`}
                    >
                      {t(s.label)}
                    </button>
                  );
                })}
              </div>
              {/* El período, con la misma forma de chips que los filtros de
                  arriba. Antes eran dos campos sueltos y un "Mes actual"
                  apretado contra el segundo. */}
              <div className="mt-4">
                <FiltroRangoFechas
                  desde={dateDesde}
                  hasta={dateHasta}
                  etiquetaDesde={t("filtro.recibidoDesde")}
                  etiquetaHasta={t("filtro.recibidoHasta")}
                  onChange={(d, h) => {
                    setDateDesde(d);
                    setDateHasta(h);
                    setPage(1);
                  }}
                />
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t("busqueda.folio")}
                  <input
                    type="text"
                    inputMode="numeric"
                    value={listFilters.folio}
                    onChange={(e) => patchListFilters({ folio: e.target.value })}
                    placeholder={t("busqueda.folioAyuda")}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm tabular-nums dark:border-white/15 dark:bg-navy-800 dark:text-white"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t("busqueda.proveedor")}
                  <input
                    type="text"
                    value={listFilters.razon_social}
                    onChange={(e) => patchListFilters({ razon_social: e.target.value })}
                    placeholder={t("busqueda.proveedorAyuda")}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t("filtro.estadoProceso")}
                  <select
                    value={listFilters.processing_status}
                    onChange={(e) =>
                      patchListFilters({
                        processing_status: e.target.value,
                      })
                    }
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
                  >
                    {STATUS_OPTS.map((o) => (
                      <option key={o.value || "all"} value={o.value}>
                        {t(o.label)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t("filtro.emailProveedor")}
                  <input
                    type="text"
                    value={listFilters.from_email}
                    onChange={(e) =>
                      patchListFilters({ from_email: e.target.value })
                    }
                    placeholder={t("filtro.emailAyuda")}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t("filtro.filasPorPagina")}
                  <select
                    value={listFilters.limit}
                    onChange={(e) =>
                      patchListFilters({ limit: Number(e.target.value) })
                    }
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
                  >
                    {LIMIT_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div className="relative mt-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
              {loading && data.length > 0 ? (
                <div className="pointer-events-none absolute inset-0 z-[1] flex justify-center bg-white/40 pt-6 dark:bg-navy-950/50">
                  <span className="h-fit rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium shadow-sm dark:border-white/10 dark:bg-navy-800">
                    Actualizando…
                  </span>
                </div>
              ) : null}
              <table className="w-full min-w-[800px] border-collapse">
                <thead>
                  {table.getHeaderGroups().map((hg) => (
                    <tr
                      key={hg.id}
                      className="border-b border-gray-200 bg-white dark:border-white/10 dark:bg-navy-900"
                    >
                      {hg.headers.map((header) => (
                        <th
                          key={header.id}
                          className="whitespace-nowrap px-3 py-3 text-left text-xs font-bold uppercase text-gray-600 dark:text-gray-300"
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-3 py-8 text-center text-sm text-gray-500"
                      >
                        {listMeta.total === 0
                          ? t("msg.sinRegistros")
                          : "Sin filas en esta página."}
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        className="cursor-pointer border-b border-gray-100 odd:bg-white even:bg-gray-50/80 transition hover:bg-brand-50/70 dark:border-white/5 dark:odd:bg-navy-900/40 dark:even:bg-navy-900/20 dark:hover:bg-brand-400/10"
                        onClick={() => openDetail(row.original.id)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="whitespace-nowrap px-3 py-2.5 align-middle">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <ServerTablePagination
              page={page}
              totalPages={listMeta.total_pages}
              total={listMeta.total}
              pageSize={pageSize}
              noun="correos"
              subtext="Ordenación en servidor (sort_by / sort_dir)."
              onPageChange={setPage}
            />
          </>
        ) : null}
      </Card>

      <RespuestaComercialModal
        isOpen={Boolean(respuestaDoc)}
        onClose={() => setRespuestaDoc(null)}
        documento={respuestaDoc}
        enviando={Boolean(respuestaDoc && manualAcceptingById[respuestaDoc.id])}
        onConfirmar={handleRespuestaComercial}
      />

      <AppModal
        isOpen={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setDetailItem(null);
        }}
        title="Detalle compra (Gmail inbox)"
        size="xl"
      >
        {detailLoading ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">Cargando…</p>
        ) : null}
        {!detailLoading && !detailItem ? (
          <p className="text-sm text-red-600">No se pudo cargar el detalle.</p>
        ) : null}
        {!detailLoading && detailItem ? (
          <GmailInboxDetailFields item={detailItem} />
        ) : null}
      </AppModal>
      <AlertDialog
        isOpen={Boolean(externalLinkPrompt.open)}
        leastDestructiveRef={externalLinkCancelRef}
        onClose={closeExternalLinkPrompt}
        isCentered
      >
        <AlertDialogOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <AlertDialogContent className="font-dm dark:bg-navy-800 dark:text-white">
          <AlertDialogHeader
            fontSize="lg"
            fontWeight="bold"
            className="text-navy-700 dark:text-white"
          >
            Salir a un enlace externo
          </AlertDialogHeader>
          <AlertDialogBody className="text-sm text-gray-600 dark:text-gray-300">
            Se abrirá una <strong>nueva pestaña</strong> del navegador
            {externalLinkPrompt.hostname ? (
              <>
                {" "}
                hacia <strong>{externalLinkPrompt.hostname}</strong>
              </>
            ) : null}
            . Esta aplicación (DTE↔NRS) seguirá abierta en esta pestaña.
          </AlertDialogBody>
          <AlertDialogFooter className="gap-2">
            <Button ref={externalLinkCancelRef} variant="ghost" onClick={closeExternalLinkPrompt}>
              Cancelar
            </Button>
            <Button colorScheme="brand" onClick={confirmOpenExternalLink}>
              Abrir enlace
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
